// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import { Iconify } from '../../../components/iconify';
import { isAnnotationWorkspaceOnly } from '../../../utils/annotationWorkspaceAccess';

// ----------------------------------------------------------------------

const navConfig = [
  // DASHBOARD
  // ----------------------------------------------------------------------
  {
    subheader: 'selfcheckout_subheader',
    items: [
      {
        title: 'dashboard',
        path: PATH_DASHBOARD.app,
        icon: <Iconify icon="solar:chart-bold-duotone" width={1} />,
      },
      {
        title: 'product_catalog',
        path: PATH_DASHBOARD.productCatalog.root,
        icon: <Iconify icon="solar:database-bold-duotone" width={1} />,
        roles: ['products:catalog:read', 'products:read'],
        condition: 'or',
        children: [
          { title: 'catalog_industrials', path: PATH_DASHBOARD.productCatalog.category('indus_food') },
          { title: 'catalog_drinks', path: PATH_DASHBOARD.productCatalog.category('drink') },
          { title: 'catalog_fruits', path: PATH_DASHBOARD.productCatalog.category('fruit') },
          { title: 'catalog_snacks', path: PATH_DASHBOARD.productCatalog.category('snack') },
        ],
      },
      {
        title: 'restaurant_products',
        path: PATH_DASHBOARD.restaurantProduct.root,
        icon: <Iconify icon="solar:cart-large-bold-duotone" width={1} />,
        roles: ['restaurant-products:read'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.restaurantProduct.list },
          { title: 'card', path: PATH_DASHBOARD.restaurantProduct.card },
          { title: 'create', path: PATH_DASHBOARD.restaurantProduct.new },
        ],
      },
      {
        title: 'menus',
        path: PATH_DASHBOARD.menus.root,
        icon: <Iconify icon="solar:calendar-mark-bold-duotone" width={1} />,
        roles: ['menu:read'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.menus.list },
          { title: 'card', path: PATH_DASHBOARD.menus.card },
        ],
      },
      {
        title: 'visualization',
        path: PATH_DASHBOARD.visualization.root,
        icon: <Iconify icon="solar:structure-bold-duotone" width={1} />,
        roles: ['annotation:read'],
        children: [
          {
            title: 'enrollment',
            path: PATH_DASHBOARD.visualization.enrollment,
          },
          { title: 'analysis', path: PATH_DASHBOARD.visualization.analysis },
        ],
      },
      {
        title: 'reporting',
        path: PATH_DASHBOARD.reporting.root,
        icon: <Iconify icon="solar:graph-bold-duotone" width={1} />,
        roles: ['reporting:read'],
        children: [
          { title: 'daily', path: PATH_DASHBOARD.reporting.daily },
          { title: 'summary_csi', path: PATH_DASHBOARD.reporting.summaryCsi },
        ],
      },
    ],
  },

  // HEALTH
  // ----------------------------------------------------------------------
  {
    subheader: 'health_subheader',
    items: [
      {
        title: 'visualization',
        path: PATH_DASHBOARD.health_visualization.root,
        icon: <Iconify icon="solar:posts-carousel-horizontal-bold-duotone" width={1} />,
        roles: ['annotation:read'],
        children: [{ title: 'health', path: PATH_DASHBOARD.health_visualization.analysis }],
      },
      {
        title: 'menus management',
        path: PATH_DASHBOARD.health_menu.root,
        icon: <Iconify icon="solar:calendar-mark-bold-duotone" width={1} />,
        roles: ['menu:read'],
        children: [
          {
            title: 'list',
            path: PATH_DASHBOARD.health_menu.list,
          },
          {
            title: 'categories',
            path: PATH_DASHBOARD.health_menu.categories,
          },
          {
            title: 'ciqual',
            path: PATH_DASHBOARD.health_menu.ciqual.root,
            roles: ['ciqual-items:read'],
            children: [
              { title: 'list', path: PATH_DASHBOARD.health_menu.ciqual.list },
              { title: 'create', path: PATH_DASHBOARD.health_menu.ciqual.new, roles: ['ciqual-items:write'] },
            ],
          },
          {
            title: 'dish profiles',
            path: PATH_DASHBOARD.health_menu.dishProfiles.root,
            roles: ['ciqual-items:read'],
            children: [{ title: 'list', path: PATH_DASHBOARD.health_menu.dishProfiles.list }],
          },
          {
            title: 'files',
            path: PATH_DASHBOARD.health_menu.files.root,
            children: [
              { title: 'import', path: PATH_DASHBOARD.health_menu.files.import },
              { title: 'status', path: PATH_DASHBOARD.health_menu.files.status },
              { title: 'prompt-editor', path: PATH_DASHBOARD.health_menu.files.promptEditor },
            ],
          },
        ],
      },
      {
        title: 'annotations',
        path: PATH_DASHBOARD.health_annotations.root,
        icon: <Iconify icon="solar:structure-bold-duotone" width={1} />,
        roles: ['annotation:write'],
        children: [{ title: 'images', path: PATH_DASHBOARD.health_annotations.images }],
      },
      {
        title: 'clients',
        path: PATH_DASHBOARD.clients.root,
        icon: <Iconify icon="solar:user-bold-duotone" width={1} />,
        roles: ['annotation:read'],
        children: [{ title: 'list', path: PATH_DASHBOARD.clients.list }],
      },
      {
        title: 'devices',
        path: PATH_DASHBOARD.devices.root,
        icon: <Iconify icon="solar:devices-bold-duotone" width={1} />,
        children: [
          { title: 'list', path: PATH_DASHBOARD.devices.list },
          { title: 'create', path: PATH_DASHBOARD.devices.new },
          {
            title: 'profiles',
            path: PATH_DASHBOARD.devices.profiles.root,
            children: [
              { title: 'list', path: PATH_DASHBOARD.devices.profiles.list },
              { title: 'create', path: PATH_DASHBOARD.devices.profiles.new },
            ],
          },
        ],
      },
    ],
  },

  // TOOLS
  // ----------------------------------------------------------------------
  {
    subheader: 'tools_subheader',
    items: [
      {
        title: 'taxonomy',
        path: PATH_DASHBOARD.taxonomy,
        icon: <Iconify icon="solar:structure-bold-duotone" width={1} />,
        roles: ['annotation:read'],
      },
      {
        title: 'annotations',
        path: PATH_DASHBOARD.annotations.root,
        icon: <Iconify icon="solar:structure-bold-duotone" width={1} />,
        roles: ['annotation:read'],
        children: [
          { title: 'workspace', path: PATH_DASHBOARD.annotations.workspace, roles: ['annotation:write'] },
          { title: 'annotation_batches', path: PATH_DASHBOARD.annotations.batches, roles: ['annotation:read'] },
          { title: 'legacy_annotations', path: PATH_DASHBOARD.annotations.legacy, roles: ['annotation:write'] },
          { title: 'check', path: PATH_DASHBOARD.annotations.check, roles: ['annotation:write'] },
          { title: 'crop', path: PATH_DASHBOARD.annotations.crop, roles: ['annotation:write'] },
          {
            title: 'ingredients_check',
            path: PATH_DASHBOARD.annotations.ingredients_check,
            roles: ['annotation:write'],
          },
          { title: 'ingredients_crop', path: PATH_DASHBOARD.annotations.ingredients_crop, roles: ['annotation:write'] },
        ],
      },
      {
        title: 'evaluation',
        path: PATH_DASHBOARD.evaluation.root,
        icon: <Iconify icon="solar:chart-bold-duotone" width={1} />,
        roles: ['analysis:read'],
        children: [
          {
            title: 'pipeline_comparisons',
            path: PATH_DASHBOARD.evaluation.pipelineComparisons,
            roles: ['analysis:read'],
          },
        ],
      },
      {
        title: 'product_tools',
        path: PATH_DASHBOARD.product.root,
        icon: <Iconify icon="solar:database-bold-duotone" width={1} />,
        roles: ['products:write', 'ingredient-product-mappings:read', 'ingredient-product-mappings:write'],
        condition: 'or',
        children: [
          { title: 'list', path: PATH_DASHBOARD.product.list, roles: ['products:write'] },
          { title: 'create', path: PATH_DASHBOARD.product.new, roles: ['products:write'] },
          {
            title: 'ingredients_mapping',
            path: PATH_DASHBOARD.product.ingredientsMapping.root,
            roles: ['ingredient-product-mappings:read', 'ingredient-product-mappings:write'],
            condition: 'or',
            children: [
              {
                title: 'list',
                path: PATH_DASHBOARD.product.ingredientsMapping.list,
                roles: ['ingredient-product-mappings:read', 'ingredient-product-mappings:write'],
                condition: 'or',
              },
            ],
          },
        ],
      },
      {
        title: 'api_sandbox',
        path: PATH_DASHBOARD.apiSandbox,
        icon: <Iconify icon="solar:cloud-upload-bold-duotone" width={1} />,
        roles: ['analysis:write'],
      },
      {
        title: 'models',
        path: PATH_DASHBOARD.models.root,
        icon: <Iconify icon="solar:reorder-bold-duotone" width={1} />,
        roles: ['admin:read'],
        children: [
          {
            title: 'feature-clf',
            path: PATH_DASHBOARD.models.featureClassifier,
          },
        ],
      },
      {
        title: 'api_documentation',
        path: PATH_DASHBOARD.apiDocumentation,
        icon: <Iconify icon="solar:hashtag-square-bold-duotone" width={1} />,
        roles: ['documentation:read'],
      },
    ],
  },

  // ADMIN
  // ----------------------------------------------------------------------
  {
    subheader: 'admin_subheader',
    roles: ['admin:read', 'user:write'],
    items: [
      {
        title: 'user',
        path: PATH_DASHBOARD.user.root,
        icon: <Iconify icon="solar:user-bold-duotone" width={1} />,
        roles: ['user:write'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.user.list },
          { title: 'create', path: PATH_DASHBOARD.user.new },
        ],
      },
      {
        title: 'restaurant',
        path: PATH_DASHBOARD.restaurant.root,
        icon: <Iconify icon="solar:shop-2-bold-duotone" width={1} />,
        roles: ['restaurant:write'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.restaurant.list },
          { title: 'create', path: PATH_DASHBOARD.restaurant.new },
        ],
      },
      {
        title: 'itentification_token',
        path: PATH_DASHBOARD.identificationToken,
        icon: <Iconify icon="solar:key-square-bold-duotone" width={1} />,
        roles: ['user:write', 'restaurant:write'],
      },
    ],
  },
];

const workspaceOnlyNavConfig = [
  {
    subheader: 'tools_subheader',
    items: [
      {
        title: 'workspace',
        path: PATH_DASHBOARD.annotations.workspace,
        icon: <Iconify icon="solar:structure-bold-duotone" width={1} />,
      },
    ],
  },
];

export const getNavConfig = (user) => (isAnnotationWorkspaceOnly(user) ? workspaceOnlyNavConfig : navConfig);

export default navConfig;
